import { MouseEvent, useCallback, useMemo } from 'react';

import { navigateTo } from 'actions/main';
import {
  sendTrackingEvent,
  setSelectedArticle,
} from 'actions/workflows/workflows';
import { NewIrArticleComponent, UserMessageComponent } from 'api/types';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectCustomConfigParams,
  selectFeatureFlags,
  selectSourcesText,
} from 'reducers/workflows';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { Box, styled, useTheme } from '@mui/material';
import {
  selectAgentChatIcon,
  selectLegibleElementColor,
  selectSize,
} from 'reducers/config';
import {
  appendAnalyticsParamsToURL,
  ReadArticleLink,
} from 'components/Article/Article';
import {
  AgathaMessageContainer,
  AuthorIcon,
} from 'components/AgathaMessage/AgathaMessage';
import { selectIsMobile } from 'reducers/main';
import { MessageBubblePosition } from 'utils/getMessageBubblePositions';
import { selectLiveChatAgentIcon } from 'reducers/live-chat';
import headerLogoIcon from 'assets/icons/header-logo.svg';
import sanitizeHtml from 'utils/sanitizeHtml';
import { isValidUrl } from 'utils/utils';

interface Props {
  agentIcon?: string;
  isAdminOnly?: boolean;
  isAuthorIconVisible?: boolean;
  isLiveChat?: boolean;
  message: string;
  renderSingleNewLineCharacters?: boolean;
  widgetComponent?: UserMessageComponent;
}

export const IrArticleTextMessage: React.FC<React.PropsWithChildren<Props>> = ({
  agentIcon,
  isAdminOnly = false,
  isAuthorIconVisible = false,
  isLiveChat = false,
  message,
  renderSingleNewLineCharacters = false,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const customConfigParams = useAppSelector(selectCustomConfigParams);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isMobile = useAppSelector(selectIsMobile);
  const size = useAppSelector(selectSize);
  const sourcesText = useAppSelector(selectSourcesText);
  const hideNav = customConfigParams?.hide_nav ?? '';
  const articleStepData = widgetComponent?.component_fields.article_step_data;
  const knowledgeArticles =
    widgetComponent?.component_fields.knowledge_articles;
  const readArticleAction =
    widgetComponent?.component_fields.read_article_action;
  const openInWidget =
    hideNav !== 'true' && readArticleAction === 'expand_in_widget';
  const featureFlags = useAppSelector(selectFeatureFlags);
  const shouldShowHtml = featureFlags.includes('should_allow_html_in_ir');

  const sendArticleTrackingEvent = useCallback(
    (
      documentID: string,
      documentIndex: number,
      traceId?: string,
      spanId?: string,
      standardized_document_id?: string,
    ) => {
      dispatch(
        sendTrackingEvent({
          current_step_id: articleStepData?.current_step_id,
          doc_id: documentID,
          doc_index: documentIndex,
          event_type: 'go-to-article',
          intent_workflow_id: articleStepData?.intent_workflow_id,
          intent_workflow_version: articleStepData?.intent_workflow_version,
          org_id: articleStepData?.org_id,
          span_id: spanId,
          standardized_document_id,
          trace_id: traceId,
        }),
      );
    },
    [
      articleStepData?.current_step_id,
      articleStepData?.intent_workflow_id,
      articleStepData?.intent_workflow_version,
      articleStepData?.org_id,
      dispatch,
    ],
  );

  const openArticleInWidget = useCallback(
    (article: NewIrArticleComponent, articleIndex: number) => {
      dispatch(setSelectedArticle(article));
      dispatch(navigateTo(ARTICLE_PAGE));
      sendArticleTrackingEvent(
        article.document_id,
        articleIndex,
        article.trace_id,
        article.span_id,
        article.standardized_article_id,
      );
    },
    [dispatch, sendArticleTrackingEvent],
  );

  const getMatchingArticleAndIndex = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!knowledgeArticles) {
        return;
      }

      const container = event.currentTarget.closest('.js-bot-message');

      if (!container) {
        return;
      }

      const links = Array.from(container.querySelectorAll('a'));
      const index = links.findIndex(link => link === event.currentTarget);

      if (index === -1) {
        return;
      }

      const article = knowledgeArticles[index];

      if (!article) {
        return;
      }

      return { article, index } as const;
    },
    [knowledgeArticles],
  );

  // Calculate current link index via the parent container, and open matching article:
  const handleOpenInWidgetLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const matchingArticleAndIndex = getMatchingArticleAndIndex(event);

      if (!matchingArticleAndIndex) {
        return;
      }

      const { article, index } = matchingArticleAndIndex;

      openArticleInWidget(article, index);
    },
    [getMatchingArticleAndIndex, openArticleInWidget],
  );

  // Calculate current link index via the parent container, and send tracking
  // event for the matching article:
  const handleNewTabLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const matchingArticleAndIndex = getMatchingArticleAndIndex(event);

      if (!matchingArticleAndIndex) {
        return;
      }

      const { article, index } = matchingArticleAndIndex;

      sendArticleTrackingEvent(
        article.document_id,
        index,
        article.trace_id,
        article.span_id,
        article.standardized_article_id,
      );
    },
    [getMatchingArticleAndIndex, sendArticleTrackingEvent],
  );

  const markdownOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: {
        a: {
          props: {
            ...((knowledgeArticles?.length || 0) > 0
              ? {
                  onClick: openInWidget
                    ? handleOpenInWidgetLinkClick
                    : handleNewTabLinkClick,
                }
              : {}),
            rel: 'noreferrer',
            target: '_blank',
          },
        },
        code: {
          component: ({
            children,
            className,
          }: {
            children: React.ReactNode;
            className: string;
          }) => {
            return (
              <code className={className}>
                {typeof children === 'string'
                  ? children.replace(/&lt;\?php/g, '<?php')
                  : children}
              </code>
            );
          },
        },
      },
      wrapper: React.Fragment,
    }),
    [
      handleNewTabLinkClick,
      handleOpenInWidgetLinkClick,
      knowledgeArticles?.length,
      openInWidget,
    ],
  );

  const formattedMessage = useMemo(() => {
    let processedMessage = message;

    /**
     * https://www.markdownguide.org/basic-syntax/#line-breaks
     *
     * lookbehind is currently like 94% supported - https://caniuse.com/js-regexp-lookbehind
     */
    if (renderSingleNewLineCharacters) {
      try {
        /**
         * literal notation will throw errors when loading the app in the browser
         * causing the app to not load. RegExp constructor will throw a syntax error
         * getting caught by the try/catch block.
         */
        const regex = new RegExp('(?<!\\n)\\n(?!\\n)', 'g');
        processedMessage = processedMessage.replace(regex, '  \n');
      } catch {
        // Do nothing (browser doesn't support lookbehind/lookahead)
      }
    }

    return processedMessage;
  }, [message, renderSingleNewLineCharacters]);

  const agentChatIcon = useAppSelector(selectAgentChatIcon);
  const liveChatAgentChatIcon = useAppSelector(selectLiveChatAgentIcon);

  const getAgentChatIcon = () => {
    if (isAdminOnly) {
      return undefined;
    }

    if (isLiveChat) {
      return agentIcon || liveChatAgentChatIcon || headerLogoIcon;
    }

    return agentChatIcon;
  };

  function getLinks() {
    if (
      readArticleAction === 'none' ||
      !knowledgeArticles ||
      (!openInWidget && knowledgeArticles.every(article => !article.link))
    ) {
      return null;
    }

    function getMessageBubblePosition(index: number): MessageBubblePosition {
      if (!knowledgeArticles) {
        return 'lone';
      }

      if (knowledgeArticles?.length === 1) {
        return 'lone';
      }

      if (index === 0) {
        return 'first-in-a-batch';
      }

      if (index === knowledgeArticles?.length - 1) {
        return 'last-in-a-batch';
      }

      return 'middle';
    }
    const validArticles = knowledgeArticles?.filter(
      article => !article.link || isValidUrl(article.link),
    );

    return (
      <>
        {validArticles && (
          <Box dir='auto' marginBottom='12px'>
            {sourcesText}:
          </Box>
        )}
        {validArticles?.map((article, articleIndex) => {
          if (openInWidget) {
            return (
              <Box
                key={article.document_id}
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flexDirection: 'row',
                  margin: `0 24px 16px ${
                    isAuthorIconVisible &&
                    articleIndex === knowledgeArticles.length - 1
                      ? '-24px'
                      : '0'
                  }`,
                }}
              >
                {isAuthorIconVisible &&
                  articleIndex === knowledgeArticles.length - 1 && (
                    <AuthorIcon
                      icon={getAgentChatIcon()}
                      isLiveChat={isLiveChat}
                    />
                  )}
                <AgathaMessageContainer
                  backgroundColor={theme.palette.colors.slate[100]}
                  forethoughtLiveChat={false}
                  isMobile={isMobile}
                  key={article.document_id}
                  marginBottom={
                    articleIndex === knowledgeArticles.length - 1
                      ? '16px'
                      : '4px'
                  }
                  messageBubblePosition={{
                    bot: getMessageBubblePosition(articleIndex),
                  }}
                  size={size}
                >
                  <StyledReadArticleLink
                    aria-label={`${sourcesText}: ${article.title}`}
                    as='button'
                    data-testid={`read-article-link-${articleIndex}`}
                    dir='auto'
                    onClick={() => {
                      openArticleInWidget(article, articleIndex);
                    }}
                    themeColor={legibleElementColor}
                  >
                    {article.title}
                  </StyledReadArticleLink>
                </AgathaMessageContainer>
              </Box>
            );
          } else if (article.link) {
            return (
              <Box
                key={article.document_id}
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flexDirection: 'row',
                  margin: `0 24px 16px ${
                    isAuthorIconVisible &&
                    articleIndex === knowledgeArticles.length - 1
                      ? '-24px'
                      : '0'
                  }`,
                }}
              >
                {isAuthorIconVisible &&
                  articleIndex === knowledgeArticles.length - 1 && (
                    <AuthorIcon
                      icon={getAgentChatIcon()}
                      isLiveChat={isLiveChat}
                    />
                  )}
                <AgathaMessageContainer
                  backgroundColor={theme.palette.colors.slate[100]}
                  forethoughtLiveChat={false}
                  isMobile={isMobile}
                  messageBubblePosition={{
                    bot: getMessageBubblePosition(articleIndex),
                  }}
                  size={size}
                >
                  <StyledReadArticleLink
                    aria-label={`${sourcesText}: ${article.title}`}
                    data-testid={`read-article-link-${articleIndex}`}
                    dir='auto'
                    href={appendAnalyticsParamsToURL(article.link)}
                    key={article.document_id}
                    onClick={() => {
                      sendArticleTrackingEvent(
                        article.document_id,
                        articleIndex,
                        article.span_id,
                        article.trace_id,
                        article.standardized_article_id,
                      );
                    }}
                    rel='noreferrer'
                    target='_blank'
                    themeColor={legibleElementColor}
                  >
                    {article.title}
                  </StyledReadArticleLink>
                </AgathaMessageContainer>
              </Box>
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  return knowledgeArticles ? (
    <Box marginInline='24px'>{getLinks()}</Box>
  ) : (
    <Markdown dir='auto' options={markdownOptions}>
      {shouldShowHtml
        ? formattedMessage
        : sanitizeHtml(formattedMessage.replace(/<\?php/g, '&lt;?php'))}
    </Markdown>
  );
};

const StyledReadArticleLink = styled(ReadArticleLink)`
  padding: 0;
  margin-top: 0;
`;
